import {useCallback, useEffect, useRef, useState} from 'react'
import * as Sentry from '@sentry/react'
import {BackendApi} from '../../../../../api/api.ts'
import {fetchDocumentContentBlob} from '../../../../../api/fetchDocumentContent.ts'
import {isAbortError} from '../../../../../lib/abortError.ts'
import {isVatDeclarationApprovalRequestNotFound} from '../../../../../lib/apiErrors.ts'
import type {DocumentContent} from '../../../../../views/DocumentDrawer/components/DocumentPreview.tsx'
import {VatDeclarationApprovalRequest} from './models.ts'

export type VatDeclarationApprovalRequestLoadError = 'not_found' | 'failed'

export interface VatDeclarationApprovalRequestState {
    approvalRequest: VatDeclarationApprovalRequest | null
    loadError: VatDeclarationApprovalRequestLoadError | null
    retry: () => void
    isAnswering: boolean
    approve: () => Promise<boolean>
    reject: (reason: string) => Promise<boolean>
    loadDocument: (documentId: string, fileName: string | null, signal: AbortSignal) => Promise<DocumentContent>
}

export function useVatDeclarationApprovalRequest(api: BackendApi, requestId: string | undefined): VatDeclarationApprovalRequestState {
    const [approvalRequest, setApprovalRequest] = useState<VatDeclarationApprovalRequest | null>(null)
    const [loadError, setLoadError] = useState<VatDeclarationApprovalRequestLoadError | null>(null)
    const [isAnswering, setIsAnswering] = useState(false)
    const loadRequestRef = useRef<AbortController | null>(null)
    const currentRequestIdRef = useRef(requestId)

    useEffect(() => {
        currentRequestIdRef.current = requestId
    }, [requestId])

    const load = useCallback((keepShownRequest: boolean): Promise<boolean> => {
        loadRequestRef.current?.abort()
        if (!requestId) {
            setLoadError('not_found')
            return Promise.resolve(false)
        }
        const controller = new AbortController()
        loadRequestRef.current = controller
        setLoadError(null)
        return api.withAbortSignal(controller.signal).Entrepreneur__getVatDeclarationApprovalRequest(requestId)
            .then((response) => {
                if (!controller.signal.aborted) {
                    setApprovalRequest(response)
                }
                return true
            })
            .catch((error) => {
                if (controller.signal.aborted || isAbortError(error)) {
                    return true
                }
                const isNotFound = isVatDeclarationApprovalRequestNotFound(error)
                if (!isNotFound) {
                    Sentry.captureException(error)
                }
                if (keepShownRequest) {
                    return false
                }
                setApprovalRequest(null)
                setLoadError(isNotFound ? 'not_found' : 'failed')
                return false
            })
    }, [api, requestId])

    useEffect(() => {
        setApprovalRequest(null)
        void load(false)
        return () => loadRequestRef.current?.abort()
    }, [load])

    const retry = useCallback(() => {
        void load(false)
    }, [load])

    const answer = useCallback(async (send: (id: string) => Promise<unknown>): Promise<boolean> => {
        if (!requestId) {
            throw new Error('Cannot answer a VAT declaration approval request without a request id')
        }
        const answeredRequestId = requestId
        let isReloaded = true
        setIsAnswering(true)
        try {
            await send(answeredRequestId)
        } finally {
            if (currentRequestIdRef.current === answeredRequestId) {
                isReloaded = await load(true)
            }
            setIsAnswering(false)
        }
        return isReloaded
    }, [load, requestId])

    const approve = useCallback(
        () => answer((id) => api.Entrepreneur__approveVatDeclaration({request_id: id})),
        [answer, api],
    )

    const reject = useCallback(
        (reason: string) => answer((id) => api.Entrepreneur__rejectVatDeclaration({request_id: id, reason})),
        [answer, api],
    )

    const loadDocument = useCallback(async (documentId: string, fileName: string | null, signal: AbortSignal): Promise<DocumentContent> => {
        const blob = await fetchDocumentContentBlob(api.withAbortSignal(signal), 'entrepreneur/get-document-content', {document_id: documentId})
        return {file_name: fileName ?? 'Document', file_blob: blob, mimetype: blob.type}
    }, [api])

    return {approvalRequest, loadError, retry, isAnswering, approve, reject, loadDocument}
}
